import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Filter, Eye, FileText, Calendar, User, Building2,
  Package, CheckCircle, XCircle, Clock, Download, X,
  ChevronLeft, ChevronRight, Printer, ChevronDown, AlertCircle, Trash2, Upload, RotateCcw, Users,
  Fuel, GitBranch, RefreshCw, MessageCircle, Droplets, Hash
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import megalogo from '../Image/megalogo.png';
import ActivityFeedModal_ItemRequest from '../Activity/ActivityFeedModal_ItemRequest';
import { useMemo } from 'react';
import * as XLSX from 'xlsx';

// ════════════════════════════════════════
// TAB DEFINITIONS
// ════════════════════════════════════════
const TABS = [
  { id: 'good-issue', label: 'Good Issue', icon: FileText, color: 'blue' },
  { id: 'branch-gatepass', label: 'Good Issue Branch Gatepass', icon: GitBranch, color: 'indigo' },
  { id: 'fuel', label: 'Good Issue Fuel', icon: Fuel, color: 'amber' },
];

// ─────────────────────────────────────────────────────────────────
// HELPERS — departments that belong to Branch or Fuel tabs
// ─────────────────────────────────────────────────────────────────
const BRANCH_DEPTS = ['branch'];
const FUEL_DEPTS = ['motorpool', 'fuel'];

const isBranchRecord = (mr) => { const dept = (mr.department || mr.departmentName || '').toLowerCase(); return BRANCH_DEPTS.some(d => dept.includes(d)); };
const isFuelRecord = (mr) => { const dept = (mr.department || mr.departmentName || '').toLowerCase(); const remarks = (mr.remarks || '').toLowerCase(); return FUEL_DEPTS.some(d => dept.includes(d)) || remarks.includes('fuel'); };
const isGoodIssueRecord = (mr) => !isBranchRecord(mr) && !isFuelRecord(mr);

// ════════════════════════════════════════
// BRANCH GATEPASS SUB-COMPONENT
// ════════════════════════════════════════
function BranchGatepass() {
  const [branchMRs, setBranchMRs] = useState([]);
  const [filteredMRs, setFilteredMRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedMR, setSelectedMR] = useState(null);
  const [selectedMRDetails, setSelectedMRDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showGatepass, setShowGatepass] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [showMRFeed, setShowMRFeed] = useState(false);
  const [selectedMRForFeed, setSelectedMRForFeed] = useState(null);
  // ✅ GI numbers fetched from DB per MR id
  const [giNumbers, setGiNumbers] = useState({});
  const itemsPerPage = 10;
  const printRef = useRef();

  const statusLabels = {
    0: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    1: { label: 'For Approval', color: 'bg-yellow-100 text-yellow-700' },
    2: { label: 'Approved', color: 'bg-green-100 text-green-700' },
    3: { label: 'Released', color: 'bg-blue-100 text-blue-700' },
    4: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  };

  useEffect(() => { fetchBranchMRs(); }, []);

  useEffect(() => {
    const q = searchText.toLowerCase();
    setFilteredMRs(branchMRs.filter(mr =>
      mr.mrNo?.toLowerCase().includes(q) ||
      mr.controlNo?.toLowerCase().includes(q) ||
      mr.requestedByName?.toLowerCase().includes(q) ||
      mr.branchSpecify?.toLowerCase().includes(q) ||
      mr.departmentName?.toLowerCase().includes(q)
    ));
    setCurrentPage(1);
  }, [searchText, branchMRs]);

  const fetchBranchMRs = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/MRHeader`);
      if (!res.ok) throw new Error('Failed to fetch MR headers');
      const data = await res.json();
      const branchOnly = data.filter(isBranchRecord);
      setBranchMRs(branchOnly);
      setFilteredMRs(branchOnly);

      // ✅ Fetch GI numbers for all branch MRs from DB
      fetchGINumbersForList(branchOnly);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch GI_Number from each MR's stored value in the DB
  const fetchGINumbersForList = async (list) => {
    const map = {};
    await Promise.all(list.map(async (mr) => {
      try {
        const res = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`);
        if (res.ok) {
          const data = await res.json();
          map[mr.id] = data.gI_Number || data.gi_Number || data.GI_Number || '—';
        } else {
          map[mr.id] = '—';
        }
      } catch {
        map[mr.id] = '—';
      }
    }));
    setGiNumbers(map);
  };

  const handleViewGatepass = async (mr) => {
    setSelectedMR(mr); setLoadingDetails(true); setShowGatepass(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
      setSelectedMRDetails(res.ok ? await res.json() : []);
    } catch { setSelectedMRDetails([]); }
    finally { setLoadingDetails(false); }
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalBody = document.body.innerHTML;
    document.body.innerHTML = `
      <html><head><style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:Arial,sans-serif; font-size:11px; background:#fff; }
        table { border-collapse:collapse; width:100%; }
        td,th { border:1px solid #000; padding:4px 6px; }
        @media print { @page { margin:10mm; size:A4; } }
      </style></head><body>${printContents}</body></html>`;
    window.print();
    document.body.innerHTML = originalBody;
    window.location.reload();
  };

  const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentMRs = filteredMRs.slice(startIdx, startIdx + itemsPerPage);
  const formattedDate = selectedMR
    ? new Date(selectedMR.mrDate).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
    : '';

  const GatepassDocument = () => (
    <div ref={printRef} style={{ width: '794px', minHeight: '1123px', padding: '30px 40px', fontFamily: 'Arial,sans-serif', fontSize: '11px', color: '#000', background: '#fff', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '6px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '16px', letterSpacing: '1px' }}>MEGA SOFT HYGIENIC PRODUCT INC.</div>
        <div style={{ fontSize: '10px', marginTop: '2px' }}>38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City</div>
        <div style={{ fontSize: '10px', marginTop: '1px' }}>Telefax : 984-10-01</div>
        <div style={{ fontWeight: 'bold', fontSize: '13px', marginTop: '4px', letterSpacing: '2px' }}>GATEPASS</div>
      </div>
      <hr style={{ border: 'none', borderTop: '1.5px solid #000', margin: '6px 0 10px 0' }} />
      <div style={{ marginBottom: '4px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY TO</span></div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '0' }}>
        <tbody>
          <tr>
            <td style={{ border: 'none', padding: '2px 4px', width: '110px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER NAME</td>
            <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
            <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', width: '320px', fontWeight: 'bold' }}>{selectedMR?.branchSpecify || selectedMR?.departmentName || ''}</td>
            <td style={{ border: 'none', padding: '2px 12px', fontWeight: 'bold', fontSize: '10px', textAlign: 'right', width: '80px' }}>WBREF:</td>
            <td style={{ padding: '2px 4px', borderBottom: '1px solid #000', fontSize: '10px', minWidth: '120px' }}>{selectedMR?.controlNo || ''}</td>
          </tr>
          <tr>
            <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px' }}>CUSTOMER ADDRESS</td>
            <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
            <td colSpan={3} style={{ borderBottom: '1px solid #000', padding: '2px 4px', fontSize: '10px' }}>&nbsp;</td>
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: '8px', marginBottom: '2px' }}><span style={{ fontWeight: 'bold', fontSize: '12px' }}>DELIVERY INSTRUCTION</span></div>
      <table style={{ width: '60%', borderCollapse: 'collapse', marginBottom: '4px' }}>
        <tbody>
          {[
            { label: 'DELIVERY DATE', value: formattedDate },
            { label: 'EXPECTED ARRIVAL DATE', value: '' },
            { label: 'TRUCK PLATE NO.', value: '' },
          ].map((row, i) => (
            <tr key={i}>
              <td style={{ border: 'none', padding: '2px 4px', fontWeight: 'bold', fontSize: '10px', width: '160px' }}>{row.label}</td>
              <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', minWidth: '160px' }}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: '4px', marginBottom: '10px' }}>
        <span style={{ fontWeight: 'bold', fontSize: '10px' }}>Request by: </span>
        <span style={{ display: 'inline-block', borderBottom: '1px solid #000', minWidth: '200px', fontSize: '10px', paddingLeft: '8px' }}>{selectedMR?.requestedByName || ''}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>{['CODE', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING'].map((h, i) => (
            <th key={i} style={{ border: '1px solid #000', padding: '5px 8px', textAlign: 'center', fontWeight: 'bold', fontSize: '10px', width: ['10%', '38%', '24%', '10%', '18%'][i] }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {selectedMRDetails.length === 0
            ? <tr><td colSpan={5} style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', color: '#999' }}>No items found</td></tr>
            : selectedMRDetails.map((item, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.itemCode || ''}</td>
                <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'left', fontSize: '10px', textTransform: 'uppercase' }}>{item.itemDescription || item.itemCode}</td>
                <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px' }}>{item.remarks || item.purpose || ''}</td>
                <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.requestedQty}</td>
                <td style={{ border: '1px solid #000', padding: '4px 8px', textAlign: 'center', fontSize: '10px', fontWeight: 'bold' }}>{item.uom?.toUpperCase() || ''}</td>
              </tr>
            ))
          }
          {Array.from({ length: Math.max(0, 5 - selectedMRDetails.length) }).map((_, i) => (
            <tr key={`empty-${i}`}>{[...Array(5)].map((__, j) => <td key={j} style={{ border: '1px solid #000', padding: '4px 8px', height: '22px' }}>&nbsp;</td>)}</tr>
          ))}
        </tbody>
      </table>
      <div style={{ height: '24px' }} />
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            {[
              { label: 'Prepared by:', name: selectedMR?.requestedByName, sub: '' },
              { label: 'Verified By:', name: selectedMR?.approvedByName, sub: '' },
              { label: 'Dispatch by:', name: selectedMR?.releasedByName, sub: 'Signature over Printed Name/Date' },
            ].map((sig, i) => (
              <td key={i} style={{ width: '33%', padding: i === 0 ? '0 20px 0 0' : i === 1 ? '0 10px' : '0 0 0 20px', verticalAlign: 'top', border: 'none' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '28px' }}>{sig.label}</div>
                  <div style={{ borderBottom: '1px solid #000', paddingBottom: '2px', fontSize: '11px', fontWeight: i === 0 ? 'bold' : 'normal', minHeight: '20px' }}>{sig.name || ''}</div>
                  {sig.sub && <div style={{ fontSize: '9px', color: '#555', marginTop: '2px' }}>{sig.sub}</div>}
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <div style={{ marginTop: '20px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '8px' }}>CUSTOMERS ACKNOWLEDGMENT:</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ border: 'none', padding: '2px 4px', width: '180px', fontSize: '10px' }}>Customer Representative</td>
              <td style={{ border: 'none', padding: '2px 4px', fontSize: '10px' }}>:</td>
              <td style={{ borderBottom: '1px solid #000', padding: '2px 8px', fontSize: '10px', width: '260px' }}>&nbsp;</td>
              <td style={{ border: 'none', padding: '2px 8px', textAlign: 'center', fontSize: '9px', color: '#555', width: '220px' }}>Signature over Printed Name/Date</td>
            </tr>
            <tr>
              <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>Status of Delivered Items</td>
              <td style={{ border: 'none', padding: '4px 4px', fontSize: '10px' }}>:</td>
              <td style={{ borderBottom: '1px solid #000', padding: '4px 8px', fontSize: '10px' }}>&nbsp;</td>
              <td style={{ border: 'none' }}>&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '18px', fontSize: '9px', color: '#333' }}>2-Customer Copy/1-Logistics Copy/-1File Copy</div>
    </div>
  );

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Branch MRs', value: branchMRs.length, color: 'text-indigo-700', bg: 'bg-indigo-50' },
          { label: 'Draft', value: branchMRs.filter(m => m.status === 0).length, color: 'text-gray-700', bg: 'bg-gray-50' },
          { label: 'For Approval', value: branchMRs.filter(m => m.status === 1).length, color: 'text-yellow-700', bg: 'bg-yellow-50' },
          { label: 'Approved / Released', value: branchMRs.filter(m => m.status >= 2).length, color: 'text-green-700', bg: 'bg-green-50' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl p-5 border border-white shadow-sm`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{s.label}</p>
            <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search MR No, Control No, Branch..." value={searchText} onChange={e => setSearchText(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
        </div>
        <button onClick={fetchBranchMRs} className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="text-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent mx-auto mb-3" /><p className="text-slate-500 text-sm font-medium">Loading Branch Gatepasses...</p></div></div>
        ) : error ? (
          <div className="flex items-center justify-center py-20 text-center"><div><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="text-slate-700 font-semibold">{error}</p><button onClick={fetchBranchMRs} className="mt-4 px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold">Retry</button></div></div>
        ) : currentMRs.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-center"><div><Building2 className="w-14 h-14 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-semibold">No Branch Gatepasses found</p></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white text-xs uppercase tracking-wider">
                  {/* ✅ Added GI No. column */}
                  {['MR No.', 'Control No.', 'GI No.', 'Date', 'Branch', 'Requested By', 'Status', 'Action'].map(h => (
                    <th key={h} className={`px-5 py-4 font-bold ${h === 'Action' ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {currentMRs.map((mr, idx) => {
                  const status = statusLabels[mr.status] || statusLabels[0];
                  return (
                    <tr key={mr.id} className={`hover:bg-indigo-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                      <td className="px-5 py-3.5"><span className="font-mono font-bold text-indigo-900 text-sm">{mr.mrNo}</span></td>
                      <td className="px-5 py-3.5"><span className="font-mono text-slate-600 text-sm">{mr.controlNo || '—'}</span></td>
                      {/* ✅ GI Number from DB */}
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-violet-200 font-mono">
                          <Hash className="w-3 h-3" />{giNumbers[mr.id] ?? '...'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-600">{new Date(mr.mrDate).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}</td>
                      <td className="px-5 py-3.5"><div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-indigo-500 flex-shrink-0" /><span className="text-sm font-semibold text-slate-800">{mr.branchSpecify || mr.departmentName || 'Branch'}</span></div></td>
                      <td className="px-5 py-3.5 text-sm text-slate-700">{mr.requestedByName}</td>
                      <td className="px-5 py-3.5"><span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${status.color}`}>{status.label}</span></td>
                      <td className="px-5 py-3.5 text-center">
                        <button onClick={() => handleViewGatepass(mr)} className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all shadow-sm">
                          <Printer className="w-3.5 h-3.5" /> View Gatepass
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
            <p className="text-sm text-slate-500">Showing {startIdx + 1}–{Math.min(startIdx + itemsPerPage, filteredMRs.length)} of {filteredMRs.length}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-semibold px-2">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
      {showGatepass && selectedMR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-800 to-indigo-900 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5" />
                <div>
                  <p className="font-bold text-lg">Gatepass — {selectedMR.mrNo}</p>
                  <p className="text-indigo-200 text-xs mt-0.5">{selectedMR.branchSpecify || selectedMR.departmentName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handlePrint} className="flex items-center gap-2 px-5 py-2 bg-white text-indigo-900 rounded-xl font-bold text-sm hover:bg-indigo-50 transition-all shadow"><Printer className="w-4 h-4" /> Print</button>
                <button onClick={() => { setShowGatepass(false); setSelectedMR(null); setSelectedMRDetails([]); }} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-gray-100">
              {loadingDetails ? <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent" /></div> : <GatepassDocument />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════
// FUEL ISSUANCE SUB-COMPONENT (unchanged)
// ════════════════════════════════════════
function FuelIssuance() {
  const [fuelRequests, setFuelRequests] = useState([]);
  const [filteredRequests, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedRequest, setSelected] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [subTab, setSubTab] = useState('list'); // 'list' | 'gi-per-day'
  const [expandedGI, setExpandedGI] = useState({});
  const itemsPerPage = 10;

  useEffect(() => {
    fetchFuelRequests();
  }, []);

  useEffect(() => {
    const q = searchText.toLowerCase();
    setFiltered(fuelRequests.filter(r =>
      r.controlNo?.toLowerCase().includes(q) ||
      r.requestorName?.toLowerCase().includes(q) ||
      r.plateNumber?.toLowerCase().includes(q) ||
      r.department?.toLowerCase().includes(q) ||
      r.gI_Fuel_No?.toLowerCase().includes(q)
    ));
    setCurrentPage(1);
  }, [searchText, fuelRequests]);

  const fetchFuelRequests = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/FuelRequisition`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data ? [data] : []);
      // Only approved
      const approved = arr.filter(r =>
        r.approvedByVisor === true || r.approvedByVisor === 1
      );
      setFuelRequests(approved);
      setFiltered(approved);
    } catch {
      setFuelRequests([]); setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : 'N/A';

  // ── Group by GI_Fuel_No ────────────────────────────────────────
  const groupedByGI = useMemo(() => {
    const map = {};
    fuelRequests.forEach(r => {
      const gi = r.gI_Fuel_No || r.GI_Fuel_No || '—';
      if (!map[gi]) map[gi] = [];
      map[gi].push(r);
    });
    // Sort by date descending
    return Object.entries(map)
      .sort((a, b) => {
        const dateA = new Date(a[1][0]?.approvalDate || 0);
        const dateB = new Date(b[1][0]?.approvalDate || 0);
        return dateB - dateA;
      });
  }, [fuelRequests]);

  // ── Export Excel ───────────────────────────────────────────────
  const exportExcel = () => {
    const rows = [];
    groupedByGI.forEach(([gi, records]) => {
      records.forEach(r => {
        rows.push({
          'GI No.': gi,
          'Control No.': r.controlNo,
          'FR No.': r.fR_Number || '—',
          'Date': formatDate(r.requestDate),
          'Requestor': r.requestorName,
          'Plate #': r.plateNumber,
          'Department': r.department || 'N/A',
          'Qty (L)': r.quantityLiters || 0,
          'Actual Liters': r.actualLiters || 0,
          'Approved By': r.approvedBy,
        });
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Fuel GI Per Day');
    XLSX.writeFile(wb, `Fuel_GI_Per_Day_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ── Export PDF (clean SO/IS style) ────────────────────────────
  const exportPDF = (request) => {
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.width;
    const ph = doc.internal.pageSize.height;
    const m = 15;
    let y = m;

    // Header box
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.rect(m, y, pw - m * 2, 28);

    // Logo placeholder area
    try { doc.addImage(megalogo, 'PNG', m + 3, y + 3, 22, 12); } catch { }

    // Company name
    doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 30, 30);
    doc.text('MEGASOFT HYGIENIC PRODUCTS, INC.', pw / 2, y + 9, { align: 'center' });
    doc.setFontSize(7); doc.setFont(undefined, 'normal'); doc.setTextColor(100, 100, 100);
    doc.text('38-E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City', pw / 2, y + 14, { align: 'center' });
    doc.text('Tel: 984-10-01', pw / 2, y + 18, { align: 'center' });

    // Doc code box (top right)
    doc.setDrawColor(180, 180, 180);
    doc.rect(pw - m - 45, y + 1, 44, 14);
    doc.setFontSize(6); doc.setTextColor(80, 80, 80);
    doc.text('DOC CODE: SPR-F-009', pw - m - 43, y + 5.5);
    doc.text('ISSUE NO: 1  |  REV: 0', pw - m - 43, y + 9.5);
    doc.text(`DATE: ${new Date().toLocaleDateString()}`, pw - m - 43, y + 13.5);

    y += 30;

    // Title
    doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text('FUEL ISSUANCE SLIP', pw / 2, y, { align: 'center' });
    doc.setDrawColor(30, 64, 175); doc.setLineWidth(0.8);
    doc.line(60, y + 2, pw - 60, y + 2);

    // Status badge
    const isApp = request.approvedByVisor === true || request.approvedByVisor === 1;
    const statusText = isApp ? 'APPROVED' : 'PENDING';
    const statusColor = isApp ? [22, 163, 74] : [202, 138, 4];
    doc.setFillColor(...statusColor);
    doc.roundedRect(pw - m - 25, y - 5, 24, 7, 1, 1, 'F');
    doc.setFontSize(6); doc.setTextColor(255, 255, 255); doc.setFont(undefined, 'bold');
    doc.text(statusText, pw - m - 13, y - 0.5, { align: 'center' });

    y += 10;

    // GI Number highlight box
    const gi = request.gI_Fuel_No || request.GI_Fuel_No || '—';
    doc.setFillColor(239, 246, 255); doc.setDrawColor(147, 197, 253); doc.setLineWidth(0.3);
    doc.rect(m, y, pw - m * 2, 10, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(30, 64, 175);
    doc.text(`GI No.: ${gi}`, m + 4, y + 7);
    doc.setFont(undefined, 'normal'); doc.setTextColor(100, 100, 100);
    doc.text(`Control No.: ${request.controlNo}`, pw / 2, y + 7);
    doc.text(`FR No.: ${request.fR_Number || '—'}`, pw - m - 4, y + 7, { align: 'right' });

    y += 15;

    // Section: Requestor Info
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('REQUESTOR INFORMATION', m, y);
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(m, y + 1.5, pw - m, y + 1.5);
    y += 6;

    const col1 = m + 2, col2 = pw / 2 + 2;
    const drawField = (label, value, x, fy) => {
      doc.setFontSize(6.5); doc.setFont(undefined, 'normal'); doc.setTextColor(130, 130, 130);
      doc.text(label, x, fy);
      doc.setFontSize(8.5); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
      doc.text(String(value || '—'), x, fy + 5);
    };

    drawField('Requestor Name', request.requestorName, col1, y);
    drawField('Department', request.department || 'N/A', col2, y);
    y += 11;
    drawField('Plate Number', request.plateNumber, col1, y);
    drawField('Date & Time', `${formatDate(request.requestDate)}  ${request.requestTime || ''}`, col2, y);
    y += 15;

    // Section: Fuel Details
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('FUEL DETAILS', m, y);
    doc.setDrawColor(200, 200, 200); doc.line(m, y + 1.5, pw - m, y + 1.5);
    y += 6;

    // Table header
    const colW = (pw - m * 2) / 4;
    doc.setFillColor(240, 240, 240); doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.2);
    doc.rect(m, y, pw - m * 2, 7, 'FD');
    doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(60, 60, 60);
    ['DESCRIPTION', 'LEVEL', 'ACTUAL LITERS', 'TRUCK ODOMETER'].forEach((h, i) => {
      doc.text(h, m + colW * i + 3, y + 5);
    });
    y += 7;

    doc.setFillColor(252, 252, 252); doc.setDrawColor(210, 210, 210);
    doc.rect(m, y, pw - m * 2, 8, 'FD');
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20);
    [
      String(request.description || '—'),
      String(request.level || '—'),
      String(request.actualLiters ?? '—') + ' L',
      String(request.truckOdometer ?? '—'),
    ].forEach((v, i) => {
      doc.text(v, m + colW * i + 3, y + 5.5);
    });
    y += 10;

    // Qty / Flow meter row
    doc.setFillColor(247, 247, 247); doc.setDrawColor(210, 210, 210);
    doc.rect(m, y, pw - m * 2, 14, 'FD');
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('Quantity of Liters Requested:', m + 3, y + 5.5);
    doc.text('Flow Meter Beg:', m + 3, y + 11);
    doc.text('Flow Meter End:', pw / 2 + 3, y + 11);
    doc.setFont(undefined, 'normal'); doc.setTextColor(20, 20, 20); doc.setFontSize(8.5);
    doc.text(String(request.quantityLiters ?? '—'), m + 57, y + 5.5);
    doc.text(String(request.flowMeterBeg ?? '—'), m + 35, y + 11);
    doc.text(String(request.flowMeterEnd ?? '—'), pw / 2 + 35, y + 11);
    y += 18;

    // Purpose
    if (request.purpose) {
      const lines = doc.splitTextToSize(request.purpose, pw - m * 2 - 20);
      const h = Math.max(12, lines.length * 5 + 8);
      doc.setFillColor(247, 247, 247); doc.rect(m, y, pw - m * 2, h, 'FD');
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text('PURPOSE:', m + 3, y + 5.5);
      doc.setFont(undefined, 'normal'); doc.setFontSize(7.5); doc.setTextColor(20, 20, 20);
      doc.text(lines, m + 22, y + 5.5);
      y += h + 6;
    }

    // Signatures
    doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
    doc.text('APPROVALS & SIGNATURES', m, y);
    doc.line(m, y + 1.5, pw - m, y + 1.5);
    y += 6;

    const sigW = (pw - m * 2 - 5) / 2;
    const drawSig = (x, fy, label, name) => {
      doc.setFillColor(255, 255, 255); doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.25);
      doc.rect(x, fy, sigW, 22, 'FD');
      doc.setFontSize(6.5); doc.setFont(undefined, 'bold'); doc.setTextColor(80, 80, 80);
      doc.text(label, x + 3, fy + 5);
      doc.setDrawColor(100, 100, 100); doc.setLineWidth(0.35);
      doc.line(x + 3, fy + 16, x + sigW - 3, fy + 16);
      if (name) { doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20); doc.text(name, x + 3, fy + 14); }
      doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140);
      doc.text('Signature & Date', x + 3, fy + 20);
    };

    drawSig(m, y, 'REQUESTED BY', request.requestedBy || request.requestorName);
    drawSig(m + sigW + 5, y, 'NOTED BY', request.notedBy);
    y += 26;
    drawSig(m, y, 'APPROVED BY', request.approvedBy);
    drawSig(m + sigW + 5, y, 'GAS MAN / ASSIST BY', request.gasManAssistBy);

    // Footer
    doc.setDrawColor(200, 200, 200); doc.setLineWidth(0.3);
    doc.line(m, ph - 12, pw - m, ph - 12);
    doc.setFontSize(6); doc.setFont(undefined, 'italic'); doc.setTextColor(150, 150, 150);
    doc.text(`Generated: ${new Date().toLocaleString()}`, pw / 2, ph - 8, { align: 'center' });
    doc.text(`Control No: ${request.controlNo}  |  GI: ${gi}  |  SPR-F-009`, pw / 2, ph - 4, { align: 'center' });

    doc.save(`Fuel_IS_${request.controlNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const currentReqs = filteredRequests.slice(startIdx, startIdx + itemsPerPage);

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Approved Fuel Requests', value: fuelRequests.length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Total GI Numbers', value: groupedByGI.length, color: 'text-violet-700', bg: 'bg-violet-50' },
          { label: 'Total Liters', value: `${fuelRequests.reduce((a, r) => a + (r.quantityLiters || 0), 0).toFixed(0)} L`, color: 'text-green-700', bg: 'bg-green-50' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl p-5 border border-white shadow-sm`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{s.label}</p>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-4 bg-slate-100 rounded-xl p-1.5 w-fit">
        {[
          { id: 'list', label: 'Fuel List' },
          { id: 'gi-per-day', label: 'Fuel GI Per Day' },
        ].map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${subTab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + Refresh */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search Control No, Requestor, GI No..." value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full pl-11 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
        </div>
        <button onClick={fetchFuelRequests}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        {subTab === 'gi-per-day' && (
          <button onClick={exportExcel}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm">
            <Download className="w-4 h-4" /> Export Excel
          </button>
        )}
      </div>

      {/* ══ TAB: FUEL LIST ══ */}
      {subTab === 'list' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Loading...</p>
              </div>
            </div>
          ) : currentReqs.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-center">
              <div><Fuel className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-semibold">No approved fuel requests found</p></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs uppercase tracking-wider">
                      {['GI No.', 'Control No.', 'FR No.', 'Date', 'Requestor', 'Plate #', 'Dept', 'Qty (L)', 'Approved By', 'Action'].map(h => (
                        <th key={h} className={`px-4 py-4 font-bold ${h === 'Action' || h === 'Qty (L)' ? 'text-center' : 'text-left'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentReqs.map((r, idx) => (
                      <tr key={r.id} className={`hover:bg-blue-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-violet-200 font-mono">
                            <Hash className="w-3 h-3" />{r.gI_Fuel_No || r.GI_Fuel_No || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-200">
                            <FileText className="w-3 h-3" />{r.controlNo}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs font-bold text-slate-600">{r.fR_Number || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{formatDate(r.requestDate)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800">{r.requestorName}</td>
                        <td className="px-4 py-3 font-mono text-sm font-bold text-slate-700">{r.plateNumber}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.department || 'N/A'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-200">
                            <Fuel className="w-3 h-3" />{r.quantityLiters || 0} L
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-green-200">
                            <CheckCircle className="w-3 h-3" />{r.approvedBy}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button onClick={() => { setSelected(r); setShowModal(true); }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold">
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <button onClick={() => exportPDF(r)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-xs font-bold">
                              <Download className="w-3 h-3" /> PDF
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                  <p className="text-sm text-slate-500">Showing {startIdx + 1}–{Math.min(startIdx + itemsPerPage, filteredRequests.length)} of {filteredRequests.length}</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
                    <span className="text-sm font-semibold px-2">{currentPage} / {totalPages}</span>
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-100"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══ TAB: FUEL GI PER DAY ══ */}
      {subTab === 'gi-per-day' && (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
          ) : groupedByGI.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-center">
              <div><Fuel className="w-14 h-14 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-semibold">No GI records found</p></div>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs uppercase tracking-wider">
                  {['GI No.', 'Date', 'Total Forms', 'Total Liters', 'Action'].map(h => (
                    <th key={h} className={`px-5 py-4 font-bold ${h === 'Action' || h === 'Total Forms' || h === 'Total Liters' ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groupedByGI.map(([gi, records]) => {
                  const isOpen = expandedGI[gi];
                  const totalLiters = records.reduce((s, r) => s + (r.quantityLiters || 0), 0);
                  const date = formatDate(records[0]?.approvalDate || records[0]?.requestDate);
                  return (
                    <React.Fragment key={gi}>
                      {/* GI Row */}
                      <tr
                        className={`cursor-pointer transition-colors ${isOpen ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-slate-50'}`}
                        onClick={() => setExpandedGI(prev => ({ ...prev, [gi]: !prev[gi] }))}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 px-3 py-1.5 rounded-lg text-sm font-bold border border-violet-200 font-mono">
                              <Hash className="w-3.5 h-3.5" />{gi}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">{date}</td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                            {records.length} forms
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
                            <Fuel className="w-3 h-3" />{totalLiters.toFixed(0)} L
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="text-xs text-blue-500 font-semibold">
                            {isOpen ? 'Click to collapse' : 'Click to expand'}
                          </span>
                        </td>
                      </tr>

                      {/* Expanded rows */}
                      {isOpen && records.map((r, i) => (
                        <tr key={r.id} className={`bg-blue-50/40 border-l-4 border-l-blue-300 ${i === records.length - 1 ? 'border-b-2 border-b-blue-200' : ''}`}>
                          <td className="px-5 py-3 pl-12">
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-200">
                              <FileText className="w-3 h-3" />{r.controlNo}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-slate-400" />
                              <span className="text-sm font-semibold text-slate-800">{r.requestorName}</span>
                              <span className="text-xs text-slate-400">•</span>
                              <span className="font-mono text-xs text-slate-500">{r.plateNumber}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-center text-sm text-slate-500">{r.department || 'N/A'}</td>
                          <td className="px-5 py-3 text-center">
                            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2.5 py-1 rounded-lg text-xs font-bold">
                              <Fuel className="w-3 h-3" />{r.quantityLiters || 0} L
                            </span>
                          </td>
                          <td className="px-5 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button onClick={(e) => { e.stopPropagation(); setSelected(r); setShowModal(true); }}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold">
                                <Eye className="w-3 h-3" /> View
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); exportPDF(r); }}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-xs font-bold">
                                <Download className="w-3 h-3" /> PDF
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* View Modal */}
      {showModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2.5 rounded-xl"><Fuel className="w-5 h-5 text-white" /></div>
                  <div>
                    <p className="text-white font-bold text-lg">Fuel Issuance Slip</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="bg-white/20 text-white text-xs font-mono font-bold px-2.5 py-1 rounded-lg">
                        {selectedRequest.controlNo}
                      </span>
                      <span className="bg-violet-400/30 text-white text-xs font-mono font-bold px-2.5 py-1 rounded-lg flex items-center gap-1">
                        <Hash className="w-3 h-3" />GI: {selectedRequest.gI_Fuel_No || selectedRequest.GI_Fuel_No || '—'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => exportPDF(selectedRequest)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-white text-blue-700 rounded-xl font-bold text-sm hover:bg-blue-50">
                    <Download className="w-4 h-4" /> PDF
                  </button>
                  <button onClick={() => setShowModal(false)}
                    className="bg-white/10 hover:bg-white/20 rounded-xl p-2.5"><X className="w-5 h-5 text-white" /></button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto bg-slate-50 p-6 space-y-4">
              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'GI Number', value: selectedRequest.gI_Fuel_No || selectedRequest.GI_Fuel_No || '—', highlight: true },
                  { label: 'FR Number', value: selectedRequest.fR_Number || '—' },
                  { label: 'Requestor', value: selectedRequest.requestorName },
                  { label: 'Department', value: selectedRequest.department || 'N/A' },
                  { label: 'Plate Number', value: selectedRequest.plateNumber },
                  { label: 'Date', value: formatDate(selectedRequest.requestDate) },
                  { label: 'Truck Odometer', value: selectedRequest.truckOdometer || 'N/A' },
                  { label: 'Approved By', value: selectedRequest.approvedBy },
                ].map((f, i) => (
                  <div key={i} className={`rounded-xl border p-4 ${f.highlight ? 'bg-violet-50 border-violet-200' : 'bg-white border-slate-200'}`}>
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{f.label}</p>
                    <p className={`text-sm font-bold ${f.highlight ? 'text-violet-800' : 'text-slate-800'}`}>{f.value}</p>
                  </div>
                ))}
              </div>
              {/* Fuel big card */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-5 flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs font-semibold uppercase mb-1">Quantity Requested</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-white">{selectedRequest.quantityLiters || 0}</span>
                    <span className="text-xl text-blue-100">Liters</span>
                  </div>
                  <p className="text-blue-200 text-xs mt-2">Actual: {selectedRequest.actualLiters || 0} L</p>
                </div>
                <div className="bg-white/20 rounded-2xl p-4"><Droplets className="w-10 h-10 text-white" /></div>
              </div>
              {/* Flow meter */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Flow Meter Beg', value: selectedRequest.flowMeterBeg ?? 'N/A' },
                  { label: 'Flow Meter End', value: selectedRequest.flowMeterEnd ?? 'N/A' },
                  { label: 'Level', value: selectedRequest.level || 'N/A' },
                ].map((f, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{f.label}</p>
                    <p className="text-sm font-bold text-slate-800 font-mono">{f.value}</p>
                  </div>
                ))}
              </div>
              {selectedRequest.purpose && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">Purpose</p>
                  <p className="text-sm text-slate-700">{selectedRequest.purpose}</p>
                </div>
              )}
            </div>
            <div className="border-t bg-white px-6 py-4 flex justify-end">
              <button onClick={() => setShowModal(false)}
                className="px-6 py-2.5 bg-slate-700 hover:bg-slate-800 text-white rounded-xl font-semibold text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════
export default function GoodIssue({ setCurrentPage }) {
  const [activeTab, setActiveTab] = useState('good-issue');
  const [showMRFeed, setShowMRFeed] = useState(false);
  const [selectedMRForFeed, setSelectedMRForFeed] = useState(null);
  const [mrList, setMrList] = useState([]);
  const [filteredMRs, setFilteredMRs] = useState([]);
  // ✅ GI numbers fetched per MR id from DB
  const [giNumbers, setGiNumbers] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDeptFilter] = useState('all');
  const [selectedMR, setSelectedMR] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [paginationPage, setPaginationPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [customLogo, setCustomLogo] = useState(null);
  const [showLogoUploader, setShowLogoUploader] = useState(false);
  const [groupedMRs, setGroupedMRs] = useState([]);
  const [selectedNameForPDF, setSelectedNameForPDF] = useState(null);
  const [sparepartsUsers, setSparepartsUsers] = useState([]);
  const [selectedReleasedBy, setSelectedReleasedBy] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedMRForReturn, setSelectedMRForReturn] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [returningItems, setReturningItems] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [returnHistory, setReturnHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [currentUser, setCurrentUser] = useState(null);



  const [customerName, setCustomerName] = useState('');


  useEffect(() => { fetchMRList(); fetchWebsiteInfo(); fetchSparepartsUsers(); }, []);
  useEffect(() => { filterMRs(); }, [searchTerm, statusFilter, departmentFilter, mrList]);
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) { try { setCurrentUser(JSON.parse(userData)); } catch { } }
  }, []);

  // ✅ Fetch GI_Number from DB for each MR in a list
  const fetchGINumbersForList = async (list) => {
    const map = {};
    await Promise.all(list.map(async (mr) => {
      // ✅ First try to use the already-loaded GI_Number from the list item
      // (avoids extra API calls if MRHeader already has gI_Number populated)
      const existing = mr.gI_Number || mr.gi_Number || mr.GI_Number;
      if (existing) {
        map[mr.id] = existing;
        return;
      }
      // Fallback: fetch individually
      try {
        const res = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`);
        if (res.ok) {
          const data = await res.json();
          map[mr.id] = data.gI_Number || data.gi_Number || data.GI_Number || '—';
        } else {
          map[mr.id] = '—';
        }
      } catch {
        map[mr.id] = '—';
      }
    }));
    setGiNumbers(map);
  };

  const fetchMRList = async () => {
    setLoading(true);
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader`);
      if (!headerRes.ok) { setMrList([]); setFilteredMRs([]); return; }
      const headerData = await headerRes.json();
      const headerArray = Array.isArray(headerData) ? headerData : (headerData ? [headerData] : []);

      const mrListWithDetails = await Promise.all(
        headerArray.map(async (header) => {
          try {
            const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${header.id}`);
            if (detailsRes.ok) { const details = await detailsRes.json(); return { ...header, mrDetails: Array.isArray(details) ? details : [] }; }
            return { ...header, mrDetails: [] };
          } catch { return { ...header, mrDetails: [] }; }
        })
      );

      const goodIssueOnly = mrListWithDetails.filter(isGoodIssueRecord);

      // ✅ Fetch GI numbers from DB for all good issue MRs
      fetchGINumbersForList(goodIssueOnly);

      setMrList(goodIssueOnly);
      setFilteredMRs(goodIssueOnly);
      groupMRsByDeptAndControl(goodIssueOnly);
    } catch { setMrList([]); setFilteredMRs([]); }
    finally { setLoading(false); }
  };

  const fetchSparepartsUsers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}api/Users`);
      if (response.ok) {
        const users = await response.json();
        const spareparts = users.filter(u => u.authorized?.toLowerCase() === 'spareparts' && u.isActive === 1);
        const usersWithSignatures = await Promise.all(spareparts.map(async (user) => {
          try {
            const signResponse = await fetch(`${API_BASE_URL}api/Users/${user.id}/digitalsign`);
            if (signResponse.ok) {
              const blob = await signResponse.blob();
              const base64 = await new Promise((resolve) => { const reader = new FileReader(); reader.onloadend = () => resolve(reader.result); reader.readAsDataURL(blob); });
              return { ...user, digitalSignFileBase64: base64.split(',')[1], digitalSignContentType: blob.type || user.digitalSignContentType };
            }
            return user;
          } catch { return user; }
        }));
        setSparepartsUsers(usersWithSignatures);
      }
    } catch { }
  };

  const groupMRsByDeptAndControl = (data) => {
    const grouped = {};
    data.forEach(mr => {
      const dept = mr.department || mr.departmentName || 'N/A';
      const controlNo = mr.controlNo || mr.mrNo || 'N/A';
      const key = `${dept}|||${controlNo}`;
      if (!grouped[key]) { grouped[key] = { department: dept, controlNo, mrs: [], names: [], allItems: [], status: mr.status, mrDate: mr.mrDate, arInvoiceNo: mr.arInvoiceNo }; }
      grouped[key].mrs.push(mr);
      grouped[key].names.push({ id: mr.id, name: mr.requestedByName, fullMR: mr });
      grouped[key].allItems.push(...(mr.mrDetails || []));
    });
    setGroupedMRs(Object.values(grouped));
  };

  const fetchWebsiteInfo = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/Website_Details`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      const activeWebsite = data.find(w => w.isActive) || data[0];
      if (activeWebsite) setWebsiteInfo(activeWebsite);
    } catch { }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { Swal.fire({ icon: 'error', title: 'Invalid File', text: 'Please upload an image file', confirmButtonColor: '#1e40af' }); return; }
    if (file.size > 2 * 1024 * 1024) { Swal.fire({ icon: 'error', title: 'File Too Large', text: 'File size must be less than 2MB', confirmButtonColor: '#1e40af' }); return; }
    const reader = new FileReader();
    reader.onloadend = () => { setCustomLogo(reader.result); setShowLogoUploader(false); Swal.fire({ icon: 'success', title: 'Logo Uploaded!', timer: 2000, confirmButtonColor: '#10b981' }); };
    reader.readAsDataURL(file);
  };

  const handleResetLogo = () => {
    Swal.fire({ title: 'Reset Logo?', icon: 'question', showCancelButton: true, confirmButtonColor: '#1e40af', cancelButtonColor: '#6b7280', confirmButtonText: 'Yes, reset it' })
      .then(r => { if (r.isConfirmed) { setCustomLogo(null); setShowLogoUploader(false); } });
  };

  const getLogoSrc = () => customLogo || megalogo;

  const filterMRs = () => {
    let filtered = [...mrList];
    if (searchTerm) filtered = filtered.filter(mr => mr.mrNo?.toLowerCase().includes(searchTerm.toLowerCase()) || mr.controlNo?.toLowerCase().includes(searchTerm.toLowerCase()) || mr.arInvoiceNo?.toLowerCase().includes(searchTerm.toLowerCase()) || mr.requestedByName?.toLowerCase().includes(searchTerm.toLowerCase()) || mr.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) || mr.department?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (statusFilter !== 'all') filtered = filtered.filter(mr => mr.status === parseInt(statusFilter));
    if (departmentFilter !== 'all') filtered = filtered.filter(mr => mr.departmentName === departmentFilter || mr.department === departmentFilter);
    setFilteredMRs(filtered);
    setPaginationPage(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      '-1': { label: 'Rejected', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle }, '0': { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: FileText }, '1': { label: 'Pending', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
      '2': { label: 'Approved', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      '3': { label: 'Issued', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
      '4': { label: 'Cancelled', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle }
    };
    const badge = badges[String(status)] || badges['0'];
    const Icon = badge.icon;
    return <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${badge.color} border inline-flex items-center gap-1.5`}><Icon className="w-3.5 h-3.5" />{badge.label}</span>;
  };

  const formatDate = (d) => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); };
  const getUniqueDepartments = () => { const depts = mrList.map(mr => mr.department || mr.departmentName).filter(Boolean); return [...new Set(depts)].sort(); };
  const getStatusCount = (s) => mrList.filter(mr => mr.status === s).length;


  // ✅ Cache para hindi mag-fetch ng paulit-ulit
  // ✅ Move cache OUTSIDE component para persistent
  let _cachedRequestors = null;
  let _cachedUsers = null;

  const getRequestors = async () => {
    if (_cachedRequestors) return _cachedRequestors;
    try {
      const res = await fetch(`${API_BASE_URL}api/Requestor`);
      if (res.ok) { _cachedRequestors = await res.json(); return _cachedRequestors; }
    } catch { }
    return [];
  };

  const getAllUsers = async () => {
    if (_cachedUsers) return _cachedUsers;
    try {
      const res = await fetch(`${API_BASE_URL}api/Users`);
      if (res.ok) { _cachedUsers = await res.json(); return _cachedUsers; }
    } catch { }
    return [];
  };

  const normalizeName = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')  // ñ → n
      .replace(/ñ/g, 'n')               // ✅ explicit ñ fallback
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ');
  };

  const fetchUserSignatureById = async (userId) => {
    const parsedId = parseInt(userId);
    if (!parsedId || isNaN(parsedId) || parsedId <= 0) return null;
    try {
      const res = await fetch(`${API_BASE_URL}api/Users/${parsedId}/digitalsign`);
      if (!res.ok) return null;
      const blob = await res.blob();
      if (!blob.type.startsWith('image/')) return null;
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  // ✅ UPDATED resolveSignature — accepts pre-fetched data, NO extra API calls
  const resolveSignatureFromCache = async (userId, name, requestors, allUsers) => {
    const normalizedInputName = normalizeName(name);

    // STEP 1: Requestor table by name — INSTANT (no API call, from cache)
    if (name && requestors.length) {
      const found = requestors.find(r => normalizeName(r.name) === normalizedInputName);
      if (found?.signature) {
        return found.signature.startsWith('data:')
          ? found.signature
          : `data:image/png;base64,${found.signature}`;
      }
    }

    // STEP 2: digitalsign by stored ID
    if (userId) {
      const sig = await fetchUserSignatureById(userId);
      if (sig) return sig;
    }

    // STEP 3: Users list name match → digitalsign — from cache, 1 API call lang
    if (name && allUsers.length) {
      const matchedUser = allUsers.find(u => {
        const full = normalizeName(`${u.firstName || ''} ${u.lastName || ''}`);
        const fullRev = normalizeName(`${u.lastName || ''} ${u.firstName || ''}`);
        return full === normalizedInputName || fullRev === normalizedInputName;
      });
      if (matchedUser?.id) {
        return await fetchUserSignatureById(matchedUser.id);
      }
    }

    return null;
  };

  // ✅ FAST handleViewMR — prefetch once, resolve parallel
  const [modalLoading, setModalLoading] = useState(false);

  // ✅ FAST handleViewMR — with loading indicator
  const handleViewMR = async (mrId) => {
    setModalLoading(true); // ← show loading agad
    try {
      const [headerRes, detailsRes, requestors, allUsers] = await Promise.all([
        fetch(`${API_BASE_URL}api/MRHeader/${mrId}`),
        fetch(`${API_BASE_URL}api/MRDetails/by-header/${mrId}`),
        getRequestors(),
        getAllUsers(),
      ]);

      if (!headerRes.ok) {
        Swal.fire({
          icon: 'error', title: 'Not Found',
          text: 'Could not load Material Request details',
          confirmButtonColor: '#1e40af'
        });
        return;
      }

      const headerData = await headerRes.json();
      let mrDetails = [];
      if (detailsRes.ok) mrDetails = await detailsRes.json();

      const [rbs, abs, rels] = await Promise.all([
        resolveSignatureFromCache(headerData.requestedBy, headerData.requestedByName, requestors, allUsers),
        resolveSignatureFromCache(headerData.approvedBy, headerData.approvedByName, requestors, allUsers),
        resolveSignatureFromCache(headerData.releasedBy, headerData.releasedByName, requestors, allUsers),
      ]);

      const capitalize = (str) =>
        str ? str.replace(/\b\w/g, (c) => c.toUpperCase()) : str;

      setSelectedMR({
        ...headerData,
        mrDetails: Array.isArray(mrDetails) ? mrDetails : [],
        isGrouped: false,
        requestedByName: capitalize(headerData.requestedByName),
        approvedByName: 'Justin Choi',
        releasedByName: capitalize(headerData.releasedByName),
        requestedBySignature: rbs,
        approvedBySignature: abs,
        releasedBySignature: rels,
        arInvoiceNo: giNumbers[mrId] || headerData.arInvoiceNo || headerData.gI_Number || headerData.gi_Number || '—',

      });
      setCustomerName(capitalize(headerData.requestedByName) || '');
      setShowModal(true);
    } catch (err) {
      console.error('handleViewMR error:', err);
      Swal.fire({
        icon: 'error', title: 'Error',
        text: 'Failed to load MR details',
        confirmButtonColor: '#1e40af'
      });
    } finally {
      setModalLoading(false); // ← hide loading
    }
  };

  // ✅ handleViewGroupedMR — same loading
  const handleViewGroupedMR = async (group) => {
    setModalLoading(true);
    try {
      setSelectedNameForPDF(group.names[0]);
      const mergedMR = {
        ...group.names[0].fullMR,
        mrDetails: group.allItems,
        groupedNames: group.names,
        isGrouped: true
      };

      const [requestors, allUsers] = await Promise.all([
        getRequestors(),
        getAllUsers(),
      ]);

      const [rbs, abs, rels] = await Promise.all([
        resolveSignatureFromCache(mergedMR.requestedBy, mergedMR.requestedByName, requestors, allUsers),
        resolveSignatureFromCache(mergedMR.approvedBy, mergedMR.approvedByName, requestors, allUsers),
        resolveSignatureFromCache(mergedMR.releasedBy, mergedMR.releasedByName, requestors, allUsers),
      ]);

      setSelectedMR({
        ...mergedMR,
        requestedBySignature: rbs,
        approvedBySignature: abs,
        releasedBySignature: rels
      });
      setShowModal(true);
    } catch (err) {
      console.error('handleViewGroupedMR error:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteMR = async (mr) => {
    const result = await Swal.fire({ title: 'Delete Material Request?', html: `<div class="text-left"><p class="mb-2"><strong>Control No:</strong> ${mr.controlNo || 'N/A'}</p><p class="mb-2"><strong>Requested By:</strong> ${mr.requestedByName}</p><p class="mt-4 text-red-600 font-semibold">⚠️ This action cannot be undone!</p></div>`, icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280', confirmButtonText: 'Yes, delete it!' });
    if (!result.isConfirmed) return;
    try {
      for (const detail of (mr.mrDetails || [])) { await fetch(`${API_BASE_URL}api/MRDetails/${detail.id}`, { method: 'DELETE' }); }
      const res = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete header');
      await Swal.fire({ icon: 'success', title: 'Deleted!', timer: 2000, confirmButtonColor: '#10b981' });
      fetchMRList();
    } catch { Swal.fire({ icon: 'error', title: 'Delete Failed', text: 'Could not delete. Please try again.', confirmButtonColor: '#dc2626' }); }
  };

  const handleOpenReturnModal = async (mr) => {
    try {
      const headerRes = await fetch(`${API_BASE_URL}api/MRHeader/${mr.id}`);
      if (!headerRes.ok) throw new Error('Failed to load MR');
      const headerData = await headerRes.json();
      const detailsRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
      let mrDetails = [];
      if (detailsRes.ok) mrDetails = await detailsRes.json();
      setSelectedMRForReturn({ ...headerData, mrDetails: Array.isArray(mrDetails) ? mrDetails : [] });
      setReturnItems(mrDetails.map(item => ({ ...item, returnQty: 0, returnReason: '' })));
      setShowReturnModal(true);
    } catch { Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load material request details', confirmButtonColor: '#dc2626' }); }
  };

  const updateReturnQty = (id, qty) => setReturnItems(returnItems.map(item => item.id === id ? { ...item, returnQty: Math.min(Math.max(0, parseFloat(qty) || 0), item.requestedQty - (item.returnedQty || 0)) } : item));
  const updateReturnReason = (id, reason) => setReturnItems(returnItems.map(item => item.id === id ? { ...item, returnReason: reason } : item));

  const handleSubmitReturns = async () => {
    const itemsToReturn = returnItems.filter(item => item.returnQty > 0);
    if (itemsToReturn.length === 0) { Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please enter quantity for items you want to return', confirmButtonColor: '#f59e0b' }); return; }
    const result = await Swal.fire({ title: 'Submit Return Request?', html: `<div class="text-left"><p class="mb-2"><strong>MR No:</strong> ${selectedMRForReturn.mrNo}</p><p class="mb-2"><strong>Items to Return:</strong> ${itemsToReturn.length}</p></div>`, icon: 'question', showCancelButton: true, confirmButtonColor: '#ea580c', cancelButtonColor: '#6b7280', confirmButtonText: 'Yes, Submit Returns' });
    if (!result.isConfirmed) return;
    setReturningItems(true);
    try {
      for (const item of itemsToReturn) {
        await fetch(`${API_BASE_URL}api/MRDetails/update/${item.id}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...item, remarks: `${item.remarks || ''}\n[RETURN SUBMITTED] ${new Date().toISOString()} - Qty: ${item.returnQty}`.trim(), returnedQty: (item.returnedQty || 0) + item.returnQty }) });
        const user = JSON.parse(localStorage.getItem("user") || '{}');
        await fetch(`${API_BASE_URL}api/ReturnRequest`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mrHeaderId: item.mrHeaderId, inventoryDataId: item.inventoryDataId, itemCode: item.itemCode, itemDescription: item.itemDescription, warehouseCode: item.warehouseCode, requestedReturnQty: item.returnQty, returnReason: item.returnReason || '', requestedBy: user?.userId || 1, requestedByName: user.firstName ? `${user.firstName} ${user.lastName}` : user.username, requestedDate: new Date().toISOString(), mrControlNo: selectedMRForReturn.controlNo || selectedMRForReturn.mrNo, status: 0 }) });
      }
      await Swal.fire({ icon: 'success', title: 'Return Request Submitted!', timer: 2500, confirmButtonColor: '#10b981' });
      setShowReturnModal(false); setSelectedMRForReturn(null); setReturnItems([]); fetchMRList();
    } catch (error) { Swal.fire({ icon: 'error', title: 'Submission Failed', text: error.message || 'Failed to submit return request', confirmButtonColor: '#dc2626' }); }
    finally { setReturningItems(false); }
  };

  const toggleReturnHistory = async (mrId) => {
    if (expandedRows[mrId]) { setExpandedRows(prev => ({ ...prev, [mrId]: false })); return; }
    setExpandedRows(prev => ({ ...prev, [mrId]: true }));
    if (returnHistory[mrId]) return;
    setLoadingHistory(prev => ({ ...prev, [mrId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest`);
      if (res.ok) { const all = await res.json(); setReturnHistory(prev => ({ ...prev, [mrId]: all.filter(r => r.mrHeaderId === mrId) })); }
    } catch { setReturnHistory(prev => ({ ...prev, [mrId]: [] })); }
    finally { setLoadingHistory(prev => ({ ...prev, [mrId]: false })); }
  };

  const getReturnStatusBadge = (status) => {
    if (status === 1) return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold">Approved</span>;
    if (status === -1) return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold">Rejected</span>;
    return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">Pending</span>;
  };

  // ── HELPER: Decode HTML entities (fixes garbled &A&p&r&i&l... issue) ──
  const decodeHtml = (str) => {
    if (!str) return str;
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    // strip non-ASCII / non-printable chars
    return txt.value.replace(/[^\x20-\x7E\u00C0-\u024F]/g, '');
  };

  const exportToPDF = (mr) => {
    const doc = new jsPDF();
    const logoSrc = getLogoSrc();
    const requestorName = mr.isGrouped && selectedNameForPDF
      ? selectedNameForPDF.name
      : mr.requestedByName;
    const pageW = doc.internal.pageSize.width;
    const m = 10;
    let y = 10;

    // ── LOGO (optional, top-left) ──
    if (logoSrc) {
      try {
        doc.addImage(logoSrc, 'PNG', m, y, 40, 20);
      } catch (e) {
        console.warn('Logo load failed:', e);
      }
    }

    // ── HEADER ──
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text('MEGA SOFT HYGIENIC PRODUCT INC.', pageW / 2, y + 6, { align: 'center' });

    doc.setFontSize(7); doc.setFont(undefined, 'normal'); doc.setTextColor(80, 80, 80);
    doc.text('38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City', pageW / 2, y + 11, { align: 'center' });
    doc.text('Telefax : 984-10-01', pageW / 2, y + 15, { align: 'center' });
    doc.text('Document Code: SPR-F-04-SP-REQ.F-001', pageW / 2, y + 19, { align: 'center' });

    doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(20, 20, 20);
    doc.text('GATEPASS', pageW / 2, y + 25, { align: 'center' });
    y += 32;

    // ── LAYOUT CONSTANTS ──
    const rowH = 12;
    const totalW = pageW - m * 2;
    const col1W = 35, col2W = 80, col3W = totalW - col1W - col2W;

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.setTextColor(0);

    // ── ROW 1: Date | Customer Name | Good Issuance No. ──
    doc.rect(m, y, col1W, rowH);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Date:', m + 2, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(8);
    doc.text(formatDate(mr.mrDate), m + 2, y + 9);

    doc.rect(m + col1W, y, col2W, rowH);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Customer Name:', m + col1W + 2, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(8);
    // ✅ FIX: decode HTML entities before writing to PDF
    const safeCustomerName = decodeHtml(customerName) || decodeHtml(requestorName) || 'N/A';
    doc.text(safeCustomerName, m + col1W + 2, y + 9);

    doc.rect(m + col1W + col2W, y, col3W, rowH);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Good Issuance No.:', m + col1W + col2W + 2, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(8);
    doc.text(mr.arInvoiceNo || '—', m + col1W + col2W + 2, y + 9);
    y += rowH;

    // ── ROW 2: Department | Control No. ──
    const deptW = col1W + col2W;
    doc.rect(m, y, deptW, rowH);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Department:', m + 2, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(8);
    doc.text(mr.department || mr.departmentName || 'N/A', m + 2, y + 9);

    doc.rect(m + deptW, y, col3W, rowH);
    doc.setFontSize(7); doc.setFont(undefined, 'bold');
    doc.text('Control No.:', m + deptW + 2, y + 4);
    doc.setFont(undefined, 'normal'); doc.setFontSize(8);
    doc.text(mr.controlNo || mr.mrNo || 'N/A', m + deptW + 2, y + 9);
    y += rowH;

    // ── ROW 3: Checkboxes Line 1 ──
    const cbRowH = 9;
    const depts1 = ['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'];
    const cbW = totalW / depts1.length;
    doc.rect(m, y, totalW, cbRowH);
    depts1.forEach((d, i) => {
      const cx = m + cbW * i + 2;
      const cy = y + 2.5;
      doc.setDrawColor(0); doc.setLineWidth(0.3);
      doc.rect(cx, cy, 3, 3);
      if (mr.department === d || mr.departmentName === d) {
        doc.setFillColor(0);
        doc.rect(cx, cy, 3, 3, 'F');
      }
      doc.setFillColor(0);
      doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(0);
      doc.text(d, cx + 4, cy + 2.5);
    });
    y += cbRowH;

    // ── ROW 4: Checkboxes Line 2 ──
    const depts2 = ['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'];
    doc.rect(m, y, totalW, cbRowH);
    depts2.forEach((d, i) => {
      const cx = m + cbW * i + 2;
      const cy = y + 2.5;
      doc.setDrawColor(0); doc.setLineWidth(0.3);
      doc.rect(cx, cy, 3, 3);
      if (mr.department === d || mr.departmentName === d) {
        doc.setFillColor(0);
        doc.rect(cx, cy, 3, 3, 'F');
      }
      doc.setFillColor(0);
      doc.setFontSize(6); doc.setFont(undefined, 'normal'); doc.setTextColor(0);
      doc.text(d, cx + 4, cy + 2.5);
    });
    if (mr.branchSpecify) {
      doc.setFontSize(6); doc.setFont(undefined, 'bold');
      doc.text(`Branch Specify: ${mr.branchSpecify}`, m + 2, y + cbRowH - 1.5);
    }
    y += cbRowH + 4;

    // ── ITEMS TABLE ──
    const tableData = (mr.mrDetails || []).map(item => [
      item.itemCode || '',
      item.itemDescription ? item.itemDescription.toUpperCase() : '',
      item.purpose || item.remarks || '',
      item.requestedQty?.toString() || '',
      item.uom?.toUpperCase() || '',
    ]);
    while (tableData.length < 6) tableData.push(['', '', '', '', '']);

    autoTable(doc, {
      startY: y,
      head: [['CODE', 'ITEM DESCRIPTION', 'REMARKS', 'QTY', 'PACKAGING']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: 0,
        fontSize: 7,
        fontStyle: 'bold',
        lineWidth: 0.3,
        lineColor: 0,
        cellPadding: 2,
        halign: 'center',
      },
      bodyStyles: {
        fontSize: 7,
        lineWidth: 0.3,
        lineColor: 0,
        cellPadding: 2,
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 22, halign: 'center' },
        1: { cellWidth: 70 },
        2: { cellWidth: 40, halign: 'center' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: totalW - 22 - 70 - 40 - 15, halign: 'center' },
      },
      margin: { left: m, right: m },
    });

    const finalY = doc.lastAutoTable.finalY + 6;

    // ── SIGNATURES (3 equal columns) ──
    const sigH = 30;
    const sigW = totalW / 3;
    const sigs = [
      {
        label: 'Requested By:',
        name: requestorName,
        sig: mr.requestedBySignature,
        hint: "(Requestor's Signature & Date)",
      },
      {
        label: 'Approved By:',
        name: mr.approvedByName,
        sig: mr.approvedBySignature,
        hint: '(Supervisor/Immediate Supervisor) Signature & Date',
      },
      {
        label: 'Released By:',
        name: mr.releasedByName,
        sig: mr.releasedBySignature,
        hint: "(Spare Parts Personnel's) Signature & Date",
      },
    ];

    sigs.forEach((s, i) => {
      const sx = m + sigW * i;
      doc.rect(sx, finalY, sigW, sigH);
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(0);
      doc.text(s.label, sx + 2, finalY + 5);

      if (s.sig) {
        try {
          doc.addImage(s.sig, 'PNG', sx + sigW / 2 - 12, finalY + 6, 24, 10);
        } catch (e) {
          console.warn(`Signature image failed:`, e);
        }
      }

      // NAME (ibabaw ng guhit)
      if (s.name) {
        doc.setFont(undefined, 'normal'); doc.setFontSize(7.5); doc.setTextColor(0);
        doc.text(decodeHtml(s.name), sx + sigW / 2, finalY + sigH - 13, { align: 'center' });
      }

      // GUHIT
      doc.setDrawColor(0); doc.setLineWidth(0.3);
      doc.line(sx + 3, finalY + sigH - 9, sx + sigW - 3, finalY + sigH - 9);

      // HINT
      doc.setFontSize(5.5); doc.setTextColor(80, 80, 80);
      doc.text(s.hint, sx + sigW / 2, finalY + sigH - 1.5, { align: 'center' });
    });

    // ── VERIFIED BY + DISPATCH BY ──
    const y2 = finalY + sigH + 4;
    const vW = totalW / 2;
    [
      { label: 'Dispatch by:', hint: 'Signature over Printed Name/Date' },
      { label: 'Verified By: ', hint: 'Signature over Printed Name/Date' },
    ].forEach((s, i) => {
      const sx = m + vW * i;
      doc.rect(sx, y2, vW, 28);
      doc.setFontSize(7); doc.setFont(undefined, 'bold'); doc.setTextColor(0);
      doc.text(s.label, sx + 2, y2 + 5);
      doc.setDrawColor(0); doc.setLineWidth(0.3);
      doc.line(sx + 3, y2 + 22, sx + vW - 3, y2 + 22);
      doc.setFontSize(5.5); doc.setFont(undefined, 'italic'); doc.setTextColor(140, 140, 140);
      doc.text(s.hint, sx + vW / 2, y2 + 27, { align: 'center' });
    });

    // ── SAVE ──
    doc.save(`SPR_${mr.controlNo || mr.mrNo}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const totalPages = Math.ceil(filteredMRs.length / itemsPerPage);
  const startIndex = (paginationPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMRs = filteredMRs.slice(startIndex, endIndex);
  const logoSrc = getLogoSrc();

  const tabColors = {
    'good-issue': { active: 'bg-blue-600 text-white', hover: 'hover:bg-blue-50 hover:text-blue-700', indicator: 'bg-blue-600' },
    'branch-gatepass': { active: 'bg-indigo-600 text-white', hover: 'hover:bg-indigo-50 hover:text-indigo-700', indicator: 'bg-indigo-600' },
    'fuel': { active: 'bg-amber-500 text-white', hover: 'hover:bg-amber-50 hover:text-amber-700', indicator: 'bg-amber-500' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">

        {/* Page Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-xl shadow-lg shadow-blue-200"><FileText className="w-8 h-8 text-white" /></div>
              <div><h1 className="text-3xl font-bold text-gray-900">Good Issuance</h1><p className="text-gray-500 mt-1">View and manage all material requests</p></div>
            </div>
            <button onClick={() => setCurrentPage('ReturnRequestList')} className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl transition-all shadow-lg font-bold">
              <RotateCcw className="w-5 h-5" /> View Return Requests
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const colors = tabColors[tab.id];
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-2.5 px-6 py-4 text-sm font-semibold transition-all flex-1 justify-center ${isActive ? colors.active : `text-gray-500 ${colors.hover}`}`}>
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {isActive && <span className={`absolute bottom-0 left-0 right-0 h-0.5 ${colors.indicator}`} />}
                </button>
              );
            })}
          </div>

          <div className="p-6">

            {/* ══ TAB 1: GOOD ISSUE ══ */}
            {activeTab === 'good-issue' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  {[
                    { icon: FileText, label: 'Draft', count: getStatusCount(0), color: 'gray' },
                    { icon: Clock, label: 'Pending', count: getStatusCount(1), color: 'amber' },
                    { icon: CheckCircle, label: 'For Release', count: getStatusCount(2), color: 'emerald' },
                    { icon: CheckCircle, label: 'Issued', count: getStatusCount(3), color: 'blue' },
                    { icon: XCircle, label: 'Rejected', count: getStatusCount(-1), color: 'red' },
                  ].map((stat, i) => {
                    const Icon = stat.icon; const c = stat.color;
                    return (
                      <div key={i} className={`bg-gradient-to-br from-${c}-50 to-${c}-100 border border-${c}-200 rounded-xl p-5`}>
                        <Icon className={`w-10 h-10 text-${c}-500 mb-2`} />
                        <p className={`text-${c}-700 text-xs font-semibold uppercase tracking-wide mb-1`}>{stat.label}</p>
                        <p className={`text-3xl font-bold text-${c}-900`}>{stat.count}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input type="text" placeholder="Search by Control No, Good Issuance No, Name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none" />
                  </div>
                  <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[180px]">
                    <option value="all">All Status</option>
                    <option value="0">Draft</option><option value="1">Pending</option><option value="2">For Release</option>
                    <option value="3">Issued</option><option value="-1">Rejected</option>
                  </select>
                  <select value={departmentFilter} onChange={e => setDeptFilter(e.target.value)} className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white min-w-[200px]">
                    <option value="all">All Departments</option>
                    {getUniqueDepartments().map(dept => (<option key={dept} value={dept}>{dept}</option>))}
                  </select>
                </div>

                {groupedMRs.filter(g => g.names.length > 1).length > 0 && (
                  <div className="bg-white rounded-2xl border border-gray-200 p-5 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Users className="w-6 h-6 text-blue-600" />
                      <h2 className="text-lg font-bold text-gray-900">Grouped Item Requests</h2>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{groupedMRs.filter(g => g.names.length > 1).length} Groups</span>
                    </div>
                    <div className="space-y-3">
                      {groupedMRs.filter(g => g.names.length > 1).map((group, idx) => (
                        <div key={idx} className="border-2 border-blue-200 rounded-xl p-4 bg-blue-50">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-1"><Building2 className="w-5 h-5 text-blue-600" /><span className="font-bold text-gray-900">{group.department}</span><span className="text-gray-400">•</span><span className="font-mono font-bold text-blue-900">{group.controlNo}</span></div>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center gap-2"><Users className="w-4 h-4" /><span>{group.names.map(n => n.name).join(', ')}</span></div>
                                <div className="flex items-center gap-2"><Package className="w-4 h-4" /><span className="font-bold text-blue-700">{group.allItems.length} Items</span></div>
                              </div>
                            </div>
                            <button onClick={() => handleViewGroupedMR(group)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-md"><Eye className="w-4 h-4" /> View Grouped</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {loading ? (
                    <div className="flex items-center justify-center py-20"><div className="text-center"><div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4" /><p className="text-gray-600 font-medium">Loading Material Requests...</p></div></div>
                  ) : filteredMRs.length === 0 ? (
                    <div className="text-center py-20"><Package className="w-16 h-16 text-gray-300 mx-auto mb-4" /><p className="text-gray-500 text-lg font-medium">No material requests found</p></div>
                  ) : (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                            <tr>
                              {['Date', 'Name', 'GI No.', 'A/R Invoice No.', 'Department', 'Control No.', 'Items', 'Status', 'Actions'].map(h => (
                                <th key={h} className={`px-6 py-4 text-xs font-bold uppercase tracking-wider ${['Items', 'Status', 'Actions'].includes(h) ? 'text-center' : 'text-left'}`}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {currentMRs.map(mr => (
                              <React.Fragment key={mr.id}>
                                <tr className={`hover:bg-blue-50 transition-colors ${expandedRows[mr.id] ? 'bg-blue-50' : ''}`}>
                                  <td className="px-6 py-4"><div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium">{formatDate(mr.mrDate)}</span></div></td>
                                  <td className="px-6 py-4"><div className="flex items-center gap-2"><User className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium">{mr.requestedByName}</span></div></td>

                                  {/* ✅ GI Number from DB state */}
                                  <td className="px-6 py-4">
                                    <span className="inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-violet-200 font-mono">
                                      <Hash className="w-3 h-3" />{giNumbers[mr.id] ?? '...'}
                                    </span>
                                  </td>

                                  <td className="px-6 py-4"><span className="text-sm font-mono font-bold text-blue-900">{mr.arInvoiceNo || '—'}</span></td>
                                  <td className="px-6 py-4"><div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium">{mr.department || mr.departmentName || 'N/A'}</span></div></td>
                                  <td className="px-6 py-4"><span className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-xs font-bold border border-blue-200"><FileText className="w-3.5 h-3.5" />{mr.controlNo || mr.mrNo}</span></td>
                                  <td className="px-6 py-4 text-center"><span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-lg text-xs font-bold border border-indigo-200"><Package className="w-3.5 h-3.5" />{mr.mrDetails?.length || 0}</span></td>
                                  <td className="px-6 py-4">
                                    <div className="flex flex-col items-center gap-1">
                                      {getStatusBadge(mr.status)}
                                      {mr.status === 2 && (
                                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-amber-200">
                                          <Clock className="w-3 h-3" />Waiting for Release
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center justify-center gap-2">


                                      <button onClick={() => toggleReturnHistory(mr.id)} className={`inline-flex items-center gap-1 px-3 py-2 rounded-xl transition-all text-sm font-semibold border-2 ${expandedRows[mr.id] ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} title="View Return History">
                                        <RotateCcw className="w-3.5 h-3.5" /><ChevronDown className={`w-3.5 h-3.5 transition-transform ${expandedRows[mr.id] ? 'rotate-180' : ''}`} />
                                      </button>
                                      <button
                                        onClick={() => { setSelectedMRForFeed(mr); setShowMRFeed(true); }}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-md"
                                      >
                                        <MessageCircle className="w-4 h-4" /> Feed
                                      </button>
                                      <button onClick={() => handleViewMR(mr.id)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold shadow-md"><Eye className="w-4 h-4" /> View</button>
                                      {(mr.status == 2 || mr.status === "2" || mr.status == 3 || mr.status === "3") && (
                                        <button onClick={() => handleOpenReturnModal(mr)} className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-sm font-semibold shadow-md"><RotateCcw className="w-4 h-4" /> Return</button>
                                      )}
                                      {currentUser?.positionId === 'SYSTEM ADMIN' && (
                                        <button onClick={() => handleDeleteMR(mr)} className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold shadow-md"><Trash2 className="w-4 h-4" /></button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {expandedRows[mr.id] && (
                                  <tr><td colSpan={9} className="px-6 py-0 bg-orange-50 border-b border-orange-200">
                                    <div className="py-4">
                                      <div className="flex items-center gap-2 mb-3"><RotateCcw className="w-4 h-4 text-orange-600" /><span className="text-sm font-bold text-orange-800">Return History for MR: {mr.controlNo || mr.mrNo}</span></div>
                                      {loadingHistory[mr.id] ? (
                                        <div className="flex items-center gap-2 py-3 text-gray-500 text-sm"><div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent" />Loading return history...</div>
                                      ) : !returnHistory[mr.id] || returnHistory[mr.id].length === 0 ? (
                                        <div className="flex items-center gap-2 py-3 text-gray-500 text-sm"><AlertCircle className="w-4 h-4" />No return requests found for this MR.</div>
                                      ) : (
                                        <table className="w-full text-sm rounded-xl overflow-hidden border border-orange-200">
                                          <thead className="bg-orange-100"><tr>{['Date', 'Item Code', 'Description', 'Return Qty', 'Reason', 'Requested By', 'Status'].map(h => <th key={h} className={`px-4 py-2 text-xs font-bold text-orange-800 uppercase ${h === 'Return Qty' || h === 'Status' ? 'text-center' : 'text-left'}`}>{h}</th>)}</tr></thead>
                                          <tbody className="bg-white divide-y divide-orange-100">
                                            {returnHistory[mr.id].map(ret => (
                                              <tr key={ret.id} className="hover:bg-orange-50">
                                                <td className="px-4 py-2 text-gray-700">{formatDate(ret.requestedDate)}</td>
                                                <td className="px-4 py-2 font-mono font-bold text-blue-900">{ret.itemCode}</td>
                                                <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{ret.itemDescription}</td>
                                                <td className="px-4 py-2 text-center"><span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">{ret.requestedReturnQty}</span></td>
                                                <td className="px-4 py-2 text-gray-600 text-xs">{ret.returnReason || '—'}</td>
                                                <td className="px-4 py-2 text-gray-700">{ret.requestedByName}</td>
                                                <td className="px-4 py-2 text-center">{getReturnStatusBadge(ret.status)}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  </td></tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {showMRFeed && selectedMRForFeed && (
                        <ActivityFeedModal_ItemRequest
                          mr={selectedMRForFeed}
                          onClose={() => { setShowMRFeed(false); setSelectedMRForFeed(null); }}
                        />
                      )}

                      {/* ✅ LOADING OVERLAY — habang nag-fe-fetch ng MR data */}
                      {modalLoading && (
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[100]">
                          <div className="bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-4 min-w-[220px]">
                            <div className="relative">
                              <div className="w-14 h-14 rounded-full border-4 border-blue-100" />
                              <div className="absolute inset-0 w-14 h-14 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
                            </div>
                            <div className="text-center">
                              <p className="text-gray-800 font-bold text-base">Loading Document</p>
                              <p className="text-gray-400 text-xs mt-1">Fetching signatures...</p>
                            </div>
                          </div>
                        </div>
                      )}
                      {totalPages > 1 && (
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                          <p className="text-sm text-gray-600">Showing <span className="font-bold">{startIndex + 1}</span> to <span className="font-bold">{Math.min(endIndex, filteredMRs.length)}</span> of <span className="font-bold">{filteredMRs.length}</span></p>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setPaginationPage(p => Math.max(1, p - 1))} disabled={paginationPage === 1} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Previous</button>
                            <div className="flex items-center gap-1">
                              {[...Array(totalPages)].map((_, idx) => {
                                const pageNum = idx + 1;
                                if (pageNum === 1 || pageNum === totalPages || (pageNum >= paginationPage - 1 && pageNum <= paginationPage + 1)) return <button key={pageNum} onClick={() => setPaginationPage(pageNum)} className={`px-4 py-2 rounded-lg text-sm font-medium ${paginationPage === pageNum ? 'bg-blue-600 text-white shadow-md' : 'border border-gray-200 hover:bg-gray-100'}`}>{pageNum}</button>;
                                else if (pageNum === paginationPage - 2 || pageNum === paginationPage + 2) return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                                return null;
                              })}
                            </div>
                            <button onClick={() => setPaginationPage(p => Math.min(totalPages, p + 1))} disabled={paginationPage === totalPages} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium disabled:opacity-50 hover:bg-gray-100 flex items-center gap-1">Next <ChevronRight className="w-4 h-4" /></button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'branch-gatepass' && <BranchGatepass />}
            {activeTab === 'fuel' && <FuelIssuance />}

          </div>
        </div>
      </div>

      {/* Good Issue View Modal */}
      {showModal && selectedMR && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-white border-b-2 border-gray-400 px-8 py-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">


                </div>
                <div className="text-center flex-1">
                  <div className="font-bold text-xl tracking-widest text-gray-900" style={{ letterSpacing: '1px' }}>
                    MEGA SOFT HYGIENIC PRODUCT INC.
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">38 - E Pleasant Drive, Pleasant View Subd., Libis, Caloocan City</div>
                  <div className="text-xs text-gray-600">Telefax : 984-10-01</div>
                  <p className="text-xs text-gray-500 mt-0.5">Document Code: <span className="font-semibold">SPR-F-04-SP-REQ.F-001</span></p>
                  <div className="font-bold text-sm tracking-widest mt-1" style={{ letterSpacing: '2px' }}>GATEPASS</div>

                  {selectedMR.isGrouped && selectedMR.groupedNames && (
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-600">Select Requestor:</span>
                      <select value={selectedNameForPDF?.id || ''} onChange={e => setSelectedNameForPDF(selectedMR.groupedNames.find(n => n.id === parseInt(e.target.value)))} className="px-3 py-1.5 border-2 border-blue-300 rounded-lg text-sm font-semibold text-blue-900 focus:border-blue-500 focus:outline-none bg-blue-50">
                        {selectedMR.groupedNames.map(nameObj => <option key={nameObj.id} value={nameObj.id}>{nameObj.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
                <div className="no-print flex gap-2">
                  <button onClick={() => exportToPDF(selectedMR)} className="p-2 hover:bg-gray-100 rounded-lg" title="Download PDF"><Download className="h-5 w-5 text-gray-700" /></button>
                  <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
                </div>
              </div>
            </div>
            <div className="p-8 bg-white flex-1 overflow-y-auto">
              <div className="border-2 border-gray-300 mb-4">
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-2 border-r-2 border-gray-300 p-3"><p className="text-xs font-bold mb-1">Date:</p><p className="text-sm">{formatDate(selectedMR.mrDate)}</p></div>
                  <div className="col-span-5 border-r-2 border-gray-300 p-3">
                    <p className="text-xs font-bold mb-1">Customer Name:</p>
                    <input
                      type="text"
                      value={customerName}
                      onChange={e => setCustomerName(e.target.value)}
                      className="text-sm w-full border-b border-gray-400 focus:outline-none focus:border-blue-500 bg-transparent"
                      placeholder="Enter customer name..."
                    />
                  </div>                  <div className="col-span-5 p-3"><p className="text-xs font-bold mb-1">Good Issuance No.:</p><p className="text-sm">{selectedMR.arInvoiceNo || '—'}</p></div>
                </div>
                <div className="grid grid-cols-12 border-b-2 border-gray-300">
                  <div className="col-span-7 border-r-2 border-gray-300 p-3"><p className="text-xs font-bold mb-1">Department:</p><p className="text-sm">{selectedMR.department || selectedMR.departmentName || 'N/A'}</p></div>
                  <div className="col-span-5 p-3"><p className="text-xs font-bold mb-1">Control No.:</p><p className="text-sm">{selectedMR.controlNo || selectedMR.mrNo}</p></div>
                </div>
                <div className="p-3 border-b-2 border-gray-300">
                  <div className="grid grid-cols-7 gap-2 text-xs">{['Mechanical', 'QC', 'Accounting', 'Logistics', 'Spareparts', 'Gen. Services', 'Production'].map(d => <label key={d} className="flex items-center gap-1"><input type="checkbox" className="w-3 h-3" disabled checked={selectedMR.department === d} /><span>{d}</span></label>)}</div>
                </div>
                <div className="p-3">
                  <div className="grid grid-cols-7 gap-2 text-xs">{['RM Whse', 'Packaging', 'Electrical', 'FG Whse', 'Motorpool', 'Admin/HR/MIS', 'Fabrication'].map(d => <label key={d} className="flex items-center gap-1"><input type="checkbox" className="w-3 h-3" disabled checked={selectedMR.department === d} /><span>{d}</span></label>)}</div>
                  {selectedMR.branchSpecify && <div className="mt-2"><span className="text-xs font-bold">Branch Specify: </span><span className="text-xs">{selectedMR.branchSpecify}</span></div>}
                </div>
              </div>
              {selectedMR.remarks && <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-4"><p className="text-xs text-amber-700 uppercase font-bold mb-1">REMARKS</p><p className="text-sm">{selectedMR.remarks}</p></div>}
              <div className="border-2 border-gray-300 overflow-hidden mb-4">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>{['Item Code', 'Qty.', 'UOM', 'Item Description', 'Purpose'].map((h, i) => <th key={h} className={`px-4 py-2 text-xs font-bold uppercase ${i < 4 ? 'border-r-2 border-gray-300' : ''} ${[1, 2].includes(i) ? 'text-center' : 'text-left'}`}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(selectedMR.mrDetails || []).map((item, index) => (
                      <tr key={item.id || index} className="border-b border-gray-300">
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm font-bold">{item.itemCode}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm">{item.requestedQty}</td>
                        <td className="px-4 py-3 text-center border-r-2 border-gray-300 text-sm">{item.uom}</td>
                        <td className="px-4 py-3 border-r-2 border-gray-300 text-sm">{item.itemDescription}</td>
                        <td className="px-4 py-3 text-sm">{item.purpose || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="bg-white border-t-2 border-gray-300 p-8 rounded-b-2xl flex-shrink-0">
              <div className="border-2 border-gray-300 p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-bold mb-2">Requested By:</p>
                    <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                      {selectedMR.requestedBySignature && <img src={selectedMR.requestedBySignature} alt="Signature" className="h-10 object-contain mb-1" />}
                      <p className="text-sm font-bold text-center">{selectedMR.isGrouped && selectedNameForPDF ? selectedNameForPDF.name : selectedMR.requestedByName}</p>
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Requestor's Signature & Date)</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold mb-2">Approved By:</p>
                    <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                      {selectedMR.approvedBySignature && <img src={selectedMR.approvedBySignature} alt="Signature" className="h-10 object-contain mb-1" />}
                      {selectedMR.approvedByName && <p className="text-sm font-bold text-center">{selectedMR.approvedByName}</p>}
                    </div>
                    <p className="text-xs text-gray-600 italic text-center">(Supervisor/Immediate Supervisor) Signature & Date</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold mb-2">Released By:</p>
                    {selectedMR.releasedByName ? (
                      <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                        {selectedMR.releasedBySignature && <img src={selectedMR.releasedBySignature} alt="Signature" className="h-10 object-contain mb-1" />}
                        <p className="text-sm font-bold text-center">{selectedMR.releasedByName}</p>
                      </div>
                    ) : (
                      <div className="h-20 flex flex-col items-center justify-end border-b-2 border-gray-800 mb-2 pb-1">
                        {selectedReleasedBy && (() => { const user = sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy)); return user?.digitalSignFileBase64 ? <img src={`data:${user.digitalSignContentType || 'image/png'};base64,${user.digitalSignFileBase64}`} alt="Signature Preview" className="h-10 object-contain mb-1" /> : null; })()}
                        {selectedReleasedBy ? (
                          <p className="text-sm font-bold text-center">{sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy))?.firstName} {sparepartsUsers.find(u => u.id === parseInt(selectedReleasedBy))?.lastName}</p>
                        ) : (
                          <select value={selectedReleasedBy} onChange={async (e) => {
                            const userId = e.target.value; setSelectedReleasedBy(userId);
                            if (userId) {
                              const selectedUser = sparepartsUsers.find(u => u.id === parseInt(userId));
                              if (selectedUser) {
                                try {
                                  const signatureData = selectedUser.digitalSignFileBase64 ? `data:${selectedUser.digitalSignContentType || 'image/png'};base64,${selectedUser.digitalSignFileBase64}` : null;
                                  const response = await fetch(`${API_BASE_URL}api/MRHeader/${selectedMR.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...selectedMR, releasedBy: selectedUser.id, releasedByName: `${selectedUser.firstName} ${selectedUser.lastName}`, sparepartByApproval: `${selectedUser.firstName} ${selectedUser.lastName}`, releasedBySignature: signatureData, releasedDate: new Date().toISOString() }) });
                                  if (response.ok) { setSelectedMR(prev => ({ ...prev, releasedBy: selectedUser.id, releasedByName: `${selectedUser.firstName} ${selectedUser.lastName}`, sparepartByApproval: `${selectedUser.firstName} ${selectedUser.lastName}`, releasedBySignature: signatureData, releasedDate: new Date().toISOString() })); Swal.fire({ icon: 'success', title: 'Saved!', html: `Released by: <strong>${selectedUser.firstName} ${selectedUser.lastName}</strong>`, timer: 1500, showConfirmButton: false }); }
                                } catch { Swal.fire({ icon: 'error', title: 'Save Failed', confirmButtonColor: '#dc2626' }); }
                              }
                            }
                          }} className="w-full px-3 py-1 border border-gray-400 rounded-lg text-sm font-semibold text-center focus:ring-2 focus:ring-blue-500 bg-white">
                            <option value="">-- Select Personnel --</option>
                            {sparepartsUsers.map(user => <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>)}
                          </select>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-gray-600 italic text-center">(Spare Parts Personnel's) Signature & Date</p>
                  </div>
                </div>

                {/* Verified By + Dispatch By */}
                <div className="grid grid-cols-2 gap-4 mt-4 border-t-2 border-gray-300 pt-4">
                  <div>
                    <p className="text-xs font-bold mb-2">Dispatch by:</p>
                    <div className="h-16 border-b-2 border-gray-800 mb-2" />
                    <p className="text-xs text-gray-600 italic text-center">Signature over Printed Name/Date</p>
                  </div>
                  <div>
                    <p className="text-xs font-bold mb-2">Verified By:</p>
                    <div className="h-16 border-b-2 border-gray-800 mb-2" />
                    <p className="text-xs text-gray-600 italic text-center">Signature over Printed Name/Date</p>
                  </div>
                </div>

              </div>
              <div className="flex justify-end mt-6">
                <button onClick={() => setShowModal(false)} className="px-6 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Uploader Modal */}
      {showLogoUploader && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4"><h3 className="text-xl font-bold">Upload Custom Logo</h3><button onClick={() => setShowLogoUploader(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button></div>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 cursor-pointer" onClick={() => document.getElementById('logoInput').click()}>
              <Upload size={48} className="mx-auto text-gray-400 mb-3" /><p className="text-gray-700 font-medium mb-1">Click to upload logo</p><p className="text-sm text-gray-500">PNG, JPG up to 2MB</p>
            </div>
            <input type="file" id="logoInput" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            {customLogo && <button onClick={handleResetLogo} className="w-full mt-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium">Reset to Default</button>}
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && selectedMRForReturn && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4"><div className="bg-white/20 p-3 rounded-xl"><RotateCcw className="w-7 h-7" /></div><div><h3 className="text-2xl font-bold">Return Items</h3><p className="text-orange-100 text-sm mt-1">MR No: {selectedMRForReturn.mrNo} | Control: {selectedMRForReturn.controlNo}</p></div></div>
                <button onClick={() => setShowReturnModal(false)} className="bg-white/10 hover:bg-white/20 rounded-xl p-3"><X className="w-6 h-6" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="bg-amber-50 border-l-4 border-amber-500 rounded-xl p-4 mb-6"><p className="text-sm text-amber-800 flex items-center gap-2"><AlertCircle className="w-4 h-4" /><strong>Enter quantity for items you want to return.</strong></p></div>
              <div className="space-y-4">
                {returnItems.map(item => (
                  <div key={item.id} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-orange-300">
                    <div className="flex items-start justify-between mb-4">
                      <div><div className="flex items-center gap-3 mb-2"><span className="font-mono font-bold text-lg text-blue-900">{item.itemCode}</span><span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">Issued: {item.requestedQty} {item.uom}</span>{item.returnedQty > 0 && <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-bold">Already Returned: {item.returnedQty} {item.uom}</span>}</div><p className="text-gray-700">{item.itemDescription}</p></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><label className="block text-sm font-semibold text-gray-700 mb-2">Return Quantity</label><input type="number" min="0" max={item.requestedQty - (item.returnedQty || 0)} step="0.01" value={item.returnQty} onChange={e => updateReturnQty(item.id, e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none font-semibold text-lg" /><p className="text-xs text-gray-500 mt-1">Max: {item.requestedQty - (item.returnedQty || 0)} {item.uom}</p></div>
                      <div><label className="block text-sm font-semibold text-gray-700 mb-2">Reason for Return</label><input type="text" value={item.returnReason} onChange={e => updateReturnReason(item.id, e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none" placeholder="e.g., Unused, Damaged..." /></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="border-t bg-gray-50 px-8 py-5 flex items-center justify-between">
              <div className="text-sm text-gray-600"><strong>{returnItems.filter(i => i.returnQty > 0).length}</strong> item(s) selected for return</div>
              <div className="flex items-center gap-3">
                <button onClick={() => setShowReturnModal(false)} className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl font-semibold">Cancel</button>
                <button onClick={handleSubmitReturns} disabled={returningItems || returnItems.every(i => i.returnQty === 0)} className="px-8 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-xl font-bold disabled:opacity-50 flex items-center gap-2">
                  {returningItems ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />Processing...</> : <><RotateCcw className="w-5 h-5" />Submit Return Request</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @media print { .no-print { display: none !important; } @page { margin: 1cm; size: A4; } }
      `}</style>
    </div>
  );
}